import { colors } from '@kmns-internal/furikake'
import {
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import {
  ResponseDatetimeSubTypeEnum,
  type ResponseTimeMeasurement,
  type ResponseTimeMeasurementRule,
  ResponseTimeMeasurementRuleTypeEnum,
  ResponseTimeMeasurementRuleUnitEnum,
  type TemplateNodeSchema,
  type TemplateQuestion,
} from '@ulysses-inc/harami_api_client'
import { type FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectDateTimeQuestionMap } from 'src/state/ducks/templates/selectors'
import { validate } from './validateMeasurementRule'

type OwnProps = {
  isOpen: boolean
  questionNode: TemplateNodeSchema
  rule?: ResponseTimeMeasurementRule
  onClose: () => void
  onSave: (questionPatch: Partial<TemplateQuestion>) => void
}

export const RuleDrawer: FC<OwnProps> = ({
  isOpen,
  questionNode,
  rule,
  onClose,
  onSave,
}) => {
  const dateTimeQuestions = useSelector(
    selectDateTimeQuestionMap(questionNode.id),
  )

  const [hasRule, setHasRule] = useState(false)
  const [hour, setHour] = useState(0)
  const [minutes, setMinutes] = useState(0)
  const [ruleType, setRuleType] = useState<number | undefined>(undefined)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- FIXME: eslint-plugin-react-hooks が 7.0.1 にバージョンアップした際にエラーになるようになったため一旦無視。ここのコードを触る際に修正してください🙏
    setHasRule(!!rule)
    setHour(Math.floor((rule?.value ?? 0) / 60))
    setMinutes((rule?.value ?? 0) % 60)
    setRuleType(rule?.type)
  }, [rule])

  const [errorMessage, setErrorMessage] = useState('')

  const handleSave = () => {
    // 計測開始時刻に設定された日時質問のフォーマットを取得
    const startQuestionNodeUUID =
      questionNode.question?.responseTimeMeasurements?.[0]
        ?.startQuestionNodeUUID ?? ''
    const startNode = dateTimeQuestions.get(startQuestionNodeUUID)
    const subType = startNode?.format

    // 分の値
    const value = hour * 60 + minutes
    const validateResult = validate(hasRule, subType, ruleType, value)
    setErrorMessage(validateResult)
    // WARN: errorMessage を検証しても、再レンダリングされるまで値が更新されないので、validateResult の方を見ること
    if (validateResult !== '') return

    // この時点でレスポンスが未設定の場合はデフォル値を設定する（必須項目なので何かしら設定しておく必要がある）
    const response = questionNode.question?.responseTimeMeasurements?.[0] ?? {
      subType: ResponseDatetimeSubTypeEnum.DATETIME,
      startQuestionNodeUUID: '',
      endQuestionNodeUUID: '',
    }

    const newResponse: ResponseTimeMeasurement = {
      ...response,
      rule:
        hasRule && !!ruleType
          ? {
              type: ruleType,
              value,
              unit: ResponseTimeMeasurementRuleUnitEnum.MINUTES,
            }
          : undefined,
    }

    // 時間計測タイプの質問にルールを設定する
    onSave({ responseTimeMeasurements: [newResponse] })

    onClose()
  }

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return
        onClose()
      }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: '100%',
            width: '100%',
            maxWidth: 600,
          },
        },
      }}
    >
      <Stack sx={{ minHeight: '100vh' }}>
        <Typography variant="h6" sx={{ my: 2, px: 3 }}>
          ルールの設定
        </Typography>
        <Divider />
        <Stack sx={{ p: 2 }} spacing={2}>
          <FormControlLabel
            control={
              <Switch checked={hasRule} onChange={() => setHasRule(!hasRule)} />
            }
            label="正常値を設定する"
          />
          {hasRule && (
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center">
                <TextField
                  type="number"
                  size="small"
                  value={hour}
                  onChange={e => setHour(Number(e.target.value))}
                  slotProps={{
                    htmlInput: { min: 0, max: 47, 'aria-label': '時間' },
                  }}
                  sx={{ width: 72, mr: 0.5 }}
                />
                <Typography sx={{ mr: 1 }}>時間</Typography>
                <TextField
                  type="number"
                  size="small"
                  value={minutes}
                  onChange={e => setMinutes(Number(e.target.value))}
                  slotProps={{ htmlInput: { min: 0, max: 59 } }}
                  sx={{ width: 72, mr: 0.5 }}
                />
                <Typography sx={{ mr: 1 }}>分</Typography>
                <Select<number | ''>
                  displayEmpty
                  size="small"
                  value={ruleType ?? ''}
                  onChange={e =>
                    setRuleType(
                      e.target.value === ''
                        ? undefined
                        : Number(e.target.value),
                    )
                  }
                  inputProps={{ 'aria-label': '正常値の判定条件' }}
                  sx={{ width: 80 }}
                >
                  <MenuItem
                    value={
                      ResponseTimeMeasurementRuleTypeEnum.LESS_THAN_OR_EQUAL
                    }
                  >
                    以下
                  </MenuItem>
                  <MenuItem
                    value={
                      ResponseTimeMeasurementRuleTypeEnum.GREATER_THAN_OR_EQUAL
                    }
                  >
                    以上
                  </MenuItem>
                </Select>
              </Stack>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  設定範囲は計測する質問のフォーマットによって違います。
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ・時刻：23時間59分まで
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ・日時：47時間59分まで
                </Typography>
              </Stack>
              {!!errorMessage && (
                <Typography color={colors.error.main}>
                  {errorMessage}
                </Typography>
              )}
            </Stack>
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="text" onClick={onClose}>
              キャンセル
            </Button>
            <Button variant="contained" onClick={handleSave}>
              保存
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  )
}
